import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  DollarSign, Users, Download, Mail, Key, ArrowLeft,
  TrendingUp, Package, RefreshCw, Shield
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="border border-[#E4E4E7] rounded-xl p-5 bg-white" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: color + "15" }}>
          <Icon size={18} style={{ color }} />
        </div>
        <span className="text-xs font-bold text-[#A1A1AA] uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-extrabold text-[#09090B]">{value}</div>
      {sub && <div className="text-xs text-[#52525B] mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const adminKey = searchParams.get("key") || "";

  useEffect(() => {
    if (!adminKey) {
      setError("Missing admin key. Add ?key=YOUR_KEY to the URL.");
      setLoading(false);
      return;
    }
    loadData();
  }, [adminKey]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [statsRes, licRes, emailRes] = await Promise.all([
        fetch(`${API}/admin/stats?key=${adminKey}`),
        fetch(`${API}/admin/licenses?key=${adminKey}`),
        fetch(`${API}/admin/emails?key=${adminKey}`),
      ]);
      if (!statsRes.ok) throw new Error("Unauthorized");
      setStats(await statsRes.json());
      setLicenses((await licRes.json()).licenses);
      setEmails((await emailRes.json()).emails);
    } catch (e) {
      setError(e.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <RefreshCw className="animate-spin text-[#A1A1AA]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="text-center">
          <Shield size={48} className="text-[#dc2626] mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#09090B] mb-2">Access Denied</h1>
          <p className="text-sm text-[#52525B] mb-4">{error}</p>
          <Link to="/" className="text-sm font-semibold text-[#FF7300] hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "licenses", label: `Licenses (${licenses.length})` },
    { id: "emails", label: `Emails (${emails.length})` },
    { id: "payments", label: `Payments (${stats?.recent_payments?.length || 0})` },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E4E4E7] px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-[#A1A1AA] hover:text-[#09090B] transition-colors">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-display font-bold text-[#09090B] text-lg">Admin Dashboard</h1>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 text-sm font-medium text-[#52525B] hover:text-[#09090B] transition-colors cursor-pointer"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={DollarSign} label="Revenue" value={`$${stats?.revenue || 0}`} sub={`${stats?.completed_payments || 0} completed`} color="#067d06" />
          <StatCard icon={Users} label="Purchases" value={stats?.total_payments || 0} sub={`${stats?.refunded_payments || 0} refunded`} color="#2563EB" />
          <StatCard icon={Download} label="Downloads" value={stats?.downloads || 0} sub="Total ZIP downloads" color="#FF7300" />
          <StatCard icon={Mail} label="Emails" value={stats?.emails_sent || 0} sub={`${stats?.emails_queued || 0} queued`} color="#8b5cf6" />
        </div>

        {/* Plan Breakdown */}
        {stats?.plan_breakdown && Object.keys(stats.plan_breakdown).length > 0 && (
          <div className="border border-[#E4E4E7] rounded-xl bg-white p-5 mb-8">
            <h3 className="font-display font-bold text-[#09090B] text-sm mb-4 flex items-center gap-2">
              <TrendingUp size={16} className="text-[#FF7300]" /> Plan Breakdown
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(stats.plan_breakdown).map(([plan, data]) => (
                <div key={plan} className="bg-[#F9FAFB] rounded-lg p-3 text-center">
                  <div className="text-xs font-bold text-[#A1A1AA] uppercase mb-1">{plan}</div>
                  <div className="text-lg font-bold text-[#09090B]">{data.count}</div>
                  <div className="text-xs text-[#52525B]">${data.revenue.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-[#F4F4F5] rounded-lg p-1 mb-6 w-fit">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                tab === t.id
                  ? "bg-white text-[#09090B] shadow-sm"
                  : "text-[#71717a] hover:text-[#09090B]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === "overview" && (
          <div className="border border-[#E4E4E7] rounded-xl bg-white overflow-hidden">
            <div className="p-5 border-b border-[#E4E4E7]">
              <h3 className="font-display font-bold text-[#09090B] text-sm flex items-center gap-2">
                <Package size={16} className="text-[#FF7300]" /> Recent Activity
              </h3>
            </div>
            {stats?.recent_payments?.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F9FAFB] text-left text-[#A1A1AA] text-xs uppercase tracking-wider">
                    <th className="px-5 py-3">Order</th>
                    <th className="px-5 py-3">Plan</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_payments.slice(0, 10).map((p, i) => (
                    <tr key={i} className="border-t border-[#F4F4F5]">
                      <td className="px-5 py-3 font-mono text-xs text-[#52525B]">{p.order_id?.slice(0, 16)}...</td>
                      <td className="px-5 py-3 font-medium text-[#09090B] capitalize">{p.plan_id}</td>
                      <td className="px-5 py-3 font-semibold text-[#09090B]">${p.amount}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                          p.status === "COMPLETED" ? "bg-[#e7f7e7] text-[#067d06]" : "bg-[#fef3cd] text-[#856404]"
                        }`}>{p.status}</span>
                      </td>
                      <td className="px-5 py-3 text-xs text-[#A1A1AA]">{p.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-12 text-center text-sm text-[#A1A1AA]">No payments yet</div>
            )}
          </div>
        )}

        {tab === "licenses" && (
          <div className="border border-[#E4E4E7] rounded-xl bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F9FAFB] text-left text-[#A1A1AA] text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">License Key</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Expires</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {licenses.length > 0 ? licenses.map((l, i) => (
                  <tr key={i} className="border-t border-[#F4F4F5]">
                    <td className="px-5 py-3 font-mono text-xs text-[#FF7300] font-bold">{l.license_key}</td>
                    <td className="px-5 py-3 capitalize font-medium">{l.plan_id}</td>
                    <td className="px-5 py-3 text-[#52525B]">{l.payer_email}</td>
                    <td className="px-5 py-3 text-xs text-[#A1A1AA]">{l.license_expiry?.slice(0, 10)}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        l.refunded ? "bg-[#fee2e2] text-[#b12704]" : "bg-[#e7f7e7] text-[#067d06]"
                      }`}>{l.refunded ? "Refunded" : "Active"}</span>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" className="p-12 text-center text-sm text-[#A1A1AA]">No licenses yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "emails" && (
          <div className="border border-[#E4E4E7] rounded-xl bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F9FAFB] text-left text-[#A1A1AA] text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">To</th>
                  <th className="px-5 py-3">Subject</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {emails.length > 0 ? emails.map((e, i) => (
                  <tr key={i} className="border-t border-[#F4F4F5]">
                    <td className="px-5 py-3 text-[#52525B]">{e.to}</td>
                    <td className="px-5 py-3 font-medium text-[#09090B]">{e.subject}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        e.sent ? "bg-[#e7f7e7] text-[#067d06]" : "bg-[#fef3cd] text-[#856404]"
                      }`}>{e.sent ? "Sent" : "Queued"}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-[#A1A1AA]">{e.created_at?.slice(0, 10)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="4" className="p-12 text-center text-sm text-[#A1A1AA]">No emails yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "payments" && (
          <div className="border border-[#E4E4E7] rounded-xl bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F9FAFB] text-left text-[#A1A1AA] text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">Order ID</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Amount</th>
                  <th className="px-5 py-3">License</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_payments?.length > 0 ? stats.recent_payments.map((p, i) => (
                  <tr key={i} className="border-t border-[#F4F4F5]">
                    <td className="px-5 py-3 font-mono text-xs text-[#52525B]">{p.order_id}</td>
                    <td className="px-5 py-3 text-[#52525B]">{p.payer_email}</td>
                    <td className="px-5 py-3 capitalize font-medium">{p.plan_id}</td>
                    <td className="px-5 py-3 font-semibold">${p.amount}</td>
                    <td className="px-5 py-3 font-mono text-xs text-[#FF7300]">{p.license_key || "—"}</td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                        p.status === "COMPLETED" ? "bg-[#e7f7e7] text-[#067d06]" : "bg-[#fef3cd] text-[#856404]"
                      }`}>{p.status}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-[#A1A1AA]">{p.created_at?.slice(0, 10)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="7" className="p-12 text-center text-sm text-[#A1A1AA]">No payments yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
